import { TraceXInterceptor } from '@trace-x/trace-x-generator';
import { Component, OnInit } from '@angular/core';

import {
  ConfigService,
  Configurations,
  DataShareService,
  OntologyResponse,
  SurfCriteriaUtil,
  SurfScrollDirective,
  SurfTextCasePipe,
  SurfUtilService
} from '@surf/surf-components-core';
import { MultiFieldInputData } from '@surf/surf-multi-field-input';
import { User } from '@tc-core/model/it/codegen/tbx/api/authority/v2/users/user';
import { DataServiceHandler } from '@tc-core/service/service-handlers/data-service-handler.service';
import { TC } from '@tc-core/util';
import { ConfigLoader, DataKey, DataStore } from '@tc-core/util/framework';
import { TariffHotelCriteria } from '../models/tariff-accom-criteria';
import { TariffFormHandlerService } from '../services/tariff-form-handler.service';
import {
  Type,
  TypeAheadService,
  TypeName,
} from '../services/type-ahead.service';
import { PARENT_TYPE } from '../utils/constants';
import { TariffSearchResultService } from '../services/tariff-search-result.service';
import { TariffApiService } from '../services/tariff-api.service';
import {Utils} from '../utils/utils';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'surf-tariff-search-criteria',
  templateUrl: './tariff-search-criteria.component.html',
})
export class TariffSearchCriteriaComponent implements OnInit {
  initialDates = [];
  selectedDest: OntologyResponse = new OntologyResponse();
  MIN_SEARCH_QUERY_LENGTH = 3;
  STATIC_DATA_FETCH_SIZE = 20;
  windowScrollEnable = true;
  focusInit = false;
  currencies = [];
  user: User;
  triggerDropDownChange = false;
  defCurrency = { code: null, name: null };
  tariffYears: { code: number; value: string; selected: boolean }[] = [];
  initialTariffYear = new Date().getFullYear();
  calendarMinDate = new Date();
  isInvalidTime = false;
  hideErrorMsgPickUp = true;
  hideErrorMsgReturn = true;
  resetPickupCalendar = false;
  resetReturnCalendar = false;
  datePickerFocus = false;
  pickUpInitialDate = [];
  returnInitialDate = [];
  returnDateWarning = false;
  pickupDateWarning = false;
  selectedPickupDate = new Date();
  tariffHotelCriteria: TariffHotelCriteria;
  geoPlaceDetailsError = '';
  surfTextCasePipe: SurfTextCasePipe;
  STATIC_DATA_URL_BASE = '/data/data-service/v2/static-data';
  EXTENDED_STATIC_DATA_FETCH_SIZE = 500;
  HTL_STATIC_DATA_TYPES: string[] = ['HOTEL'];
  GEO_ENABLE_FOR_HOTEL_SEARCH = false;
  GEO_HTL_AUTO_SWITCH_ENABLED = false;
  GEO_HTL_AUTO_SWITCH_RETRY_TIME = 60;
  DEFAULT_COUNTRY = '';
  cacheCity: string;
  cacheCountry: string;
  selectedReturnDate = new Date();
  keyParams: any;
  country = '';
  cityFilteredList = [];
  city = '';
  selectedCity: OntologyResponse = new OntologyResponse();
  selectedCountry: OntologyResponse = new OntologyResponse();
  selectedCountryCode = '';
  selectedCountryName = '';
  // tslint:disable-next-line:variable-name
  _countries = [];
  // tslint:disable-next-line:variable-name
  _cities = [];
  filteredCountryList = [];
  // tslint:disable-next-line:variable-name
  _cityURL = '';

  constructor(
    protected surfCriteriaUtil: SurfCriteriaUtil,
    private dataStore: DataStore,
    private dataServiceHandler: DataServiceHandler,
    private configService: ConfigService,
    private configLoader: ConfigLoader,
    private tariffApiService: TariffApiService,
    private dataShare: DataShareService,
    public formHandlerService: TariffFormHandlerService,
    public typeAheadService: TypeAheadService,
    private tariffResultService: TariffSearchResultService,
  ) {}

  set countries(value) {
    this._countries = value;
    this.formHandlerService.setCountries(this._countries);
  }

  get countries() {
    return this.formHandlerService._countries;
  }

  set cityURL(value) {
    this._cityURL = value;
  }

  get cityURL() {
    return this._cityURL;
  }

  set cities(value) {
    this._cities = value;
    this.formHandlerService.setCities(this._cities);
  }

  get cities() {
    return this.formHandlerService._cities;
  }


  ngOnInit(): void {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.ngOnInit');
    this.user = this.dataStore.get(DataKey.userDetail).getValue();
    this.tariffHotelCriteria = new TariffHotelCriteria();
    this.surfTextCasePipe = new SurfTextCasePipe(this.configService);
    this.loadInitialData();
  }

  loadInitialData() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.loadInitialData');
    this.loadConfigurations();
    this.loadMisc();
    this.setCurrencies();
    this.generateTariffYears();
    this.selectedReturnDate = this.getSixMonthsFromNow(this.selectedPickupDate);
    this.tariffHotelCriteria.hotelToDate = this.formattedDate(
      this.selectedReturnDate
    );
    this.tariffHotelCriteria.hotelFromDate = this.formattedDate(
      this.selectedPickupDate
    );
    this.pickUpInitialDate = [this.selectedPickupDate];
    this.returnInitialDate = [this.selectedReturnDate];
    this.tariffHotelCriteria.noOfResults = 10000;
    this.tariffHotelCriteria.rowId = 0;
    this.setupTypeAheadFields();
  }

  setupTypeAheadFields() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.setupTypeAheadFields');
    const hotelURL = this.generateStaticDataUrl('HOTEL');
    this.typeAheadService.updateTypeAheadStore('hotel', { URL: hotelURL });

    const countryURL = this.generateStaticDataUrl('COUNTRY');
    this.typeAheadService.updateTypeAheadStore('country', { URL: countryURL });

    this.updateCityRetrieveURL();
    this.typeAheadService.updateTypeAheadStore('city', { URL: this.cityURL });
  }

  loadMisc() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.loadMisc');
    this.dataShare.getKeyParamString().subscribe((keyParams) => {
      if (keyParams) {
        this.keyParams = keyParams;
      }
    });

    this.dataServiceHandler.getCountries().subscribe(
      (res) => {
        if (res) {
          this.countries = res;
          if (this.DEFAULT_COUNTRY) {
            const country = this.countries.find((item) => item.code === this.DEFAULT_COUNTRY);
            if (country) {
              this.selectCountry(country, 'country');
            }
          }
        } else {
          this.countries = [];
        }
      },
      (error) => {
        console.log('Error in getting Countries');
      }
    );
  }

  getSixMonthsFromNow(startDate: Date) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.getSixMonthsFromNow');
    // selectedReturnDate should be 6 months from now
    // ex: if today is 11th of Oct, 2023, then the selectedReturnDate should be 31st of March, 2024

    const date = SurfUtilService.getDeepClone(startDate);
    const sixMonthsFromNow = new Date(date.setMonth(date.getMonth() + 6));
    const lastDayOfSixMonthsFromNow = new Date(
      sixMonthsFromNow.getFullYear(),
      sixMonthsFromNow.getMonth(),
      0
    );

    return lastDayOfSixMonthsFromNow;
  }

  generateTariffYears() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.generateTariffYears');
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 3; i++) {
      const year = currentYear + i;
      const tariffYear = {
        code: year,
        value: `${year} - ${year + 1}`,
        selected: i === 0,
      };
      this.tariffYears.push(tariffYear);
    }
  }

  loadConfigurations() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.loadConfigurations');
    this.STATIC_DATA_URL_BASE = this.configLoader.configurations.get(
      TC.CONF.CONF_ENDPOINT
    )[TC.ENDPOINT_ID.STATIC_DATA]
      ? this.configLoader.configurations.get(TC.CONF.CONF_ENDPOINT)[
          TC.ENDPOINT_ID.STATIC_DATA
        ]
      : this.STATIC_DATA_URL_BASE;
    this.MIN_SEARCH_QUERY_LENGTH = +this.configLoader.getModuleConfig(
      Configurations.MIN_SEARCH_QUERY_LENGTH,
      TC.MODULE_NAME.SURF_B2B
    );
    this.STATIC_DATA_FETCH_SIZE = +this.configLoader.getModuleConfig(
      Configurations.STATIC_DATA_FETCH_SIZE,
      TC.MODULE_NAME.SURF_B2B
    );
    this.windowScrollEnable =
      this.configLoader
        .getModuleConfig('WINDOW_SCROLL_ENABLE', TC.MODULE_NAME.SURF_B2B)
        ?.toLowerCase() === 'true';
    this.EXTENDED_STATIC_DATA_FETCH_SIZE = +this.configLoader.getModuleConfig(
      'EXTENDED_STATIC_DATA_FETCH_SIZE',
      TC.MODULE_NAME.SURF_B2B
    );
    this.HTL_STATIC_DATA_TYPES = this.configLoader
      .getModuleConfig('HTL_STATIC_DATA_TYPES', TC.MODULE_NAME.SURF_B2B)
      ?.split(',');
    this.DEFAULT_COUNTRY = this.configLoader.getModuleConfig(
      'SURF_TARIFF_DEFAULT_COUNTRY',
      TC.MODULE_NAME.SURF_B2B
    );

    const GEO_ENABLE_FOR_HOTEL_SEARCH = this.configLoader.getModuleConfig(
      'GEO_ENABLE_FOR_HOTEL_SEARCH',
      TC.MODULE_NAME.SURF_B2B
    );
    if (GEO_ENABLE_FOR_HOTEL_SEARCH) {
      this.GEO_ENABLE_FOR_HOTEL_SEARCH =
        GEO_ENABLE_FOR_HOTEL_SEARCH.toString().toUpperCase() === 'TRUE';
    }
    const GEO_HTL_AUTO_SWITCH_RETRY_TIME_CONFIG =
      this.configLoader.getModuleConfig(
        'GEO_HTL_AUTO_SWITCH_RETRY_TIME',
        TC.MODULE_NAME.SURF_B2B
      );
    if (
      GEO_HTL_AUTO_SWITCH_RETRY_TIME_CONFIG &&
      !isNaN(parseInt(GEO_HTL_AUTO_SWITCH_RETRY_TIME_CONFIG.toString(), 10))
    ) {
      this.GEO_HTL_AUTO_SWITCH_RETRY_TIME = parseInt(
        GEO_HTL_AUTO_SWITCH_RETRY_TIME_CONFIG.toString(),
        10
      );
    }
  }

  scrollTo(selector) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.scrollTo');
    if (this.windowScrollEnable) {
      SurfScrollDirective.scrollTo(selector, null, false, false, 0, 60);
    }
  }

  // Currency stuff
  setCurrencies(): void {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.setCurrencies');
    let defaultCurrency;
    if (
      this.user &&
      this.user.userDetail &&
      this.user.userDetail.defaultCurrency &&
      this.user.userDetail.defaultCurrency.code
    ) {
      const keyParamObj = Utils.keyParmToObj(this.keyParams);
      defaultCurrency = this.user.userDetail.defaultCurrency;
      this.defCurrency = defaultCurrency;
      this.currencies = [];
      if (defaultCurrency.code !== keyParamObj?.cur) {
        defaultCurrency.code = keyParamObj.cur;
      }

      this.dataServiceHandler.getCurrencies(true).subscribe((res) => {
        if (res) {
          res.forEach((item) => {
            if (defaultCurrency.code === item.code && defaultCurrency.name !== item.name) {
              defaultCurrency.name = item.name;
            }
            this.currencies.push(
              new MultiFieldInputData(
                item.code,
                item.name + ' (' + item.code + ')',
                defaultCurrency.code === item.code
              )
            );
          });
          this.selectCurrency(defaultCurrency);
          this.triggerDropDownChange = !this.triggerDropDownChange;
        }
      });
    } else {
      defaultCurrency = {
        code: this.defCurrency.code,
        value: this.defCurrency.name,
        selected: true,
      };
      this.defCurrency = defaultCurrency;
    }
    this.selectCurrency(defaultCurrency);
    this.triggerDropDownChange = !this.triggerDropDownChange;
  }

  selectCurrency(event) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.selectCurrency');
    this.tariffHotelCriteria.hotelCurrency = event.code;
  }

  selectTariffYear(event, isForced = false) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.selectTariffYear');
    event.code =
      typeof event.code === 'number' ? event.code : Number(event.code);
    if (!isForced) {
      this.tariffHotelCriteria.hotelTariffYear = event.code;
      this.resetDates(event.code);
    } else {
      this.tariffYears.forEach((line) => {
        if (event.code !== '') {
          if (event.code === line.code) {
            line.selected = true;
            if (this.tariffHotelCriteria) {
              this.tariffHotelCriteria.hotelTariffYear = event.code;
            }
          } else {
            line.selected = false;
          }
        } else {
          line.selected = false;
          if (this.tariffHotelCriteria) {
            this.tariffHotelCriteria.hotelTariffYear = null;
          }
        }
      });
      this.tariffYears = SurfUtilService.getDeepClone(this.tariffYears);
    }
  }

  resetTariffYear() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.resetTariffYear');
    this.tariffYears.forEach((ele) => {
      ele.selected = ele.code === this.initialTariffYear;
    });
    this.tariffYears = SurfUtilService.getDeepClone(this.tariffYears);
    if (this.tariffHotelCriteria) {
      this.tariffHotelCriteria.hotelTariffYear = this.initialTariffYear;
    }
  }

  getSelectedDateTime(event, isPickup: boolean) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.getSelectedDateTime');
    this.resetReturnCalendar = false;
    if (event && event.length > 0) {
      if (isPickup) {
        this.selectedPickupDate = event[0];
        if (
          this.selectedReturnDate &&
          this.selectedReturnDate.getTime() < this.selectedPickupDate.getTime()
        ) {
          this.selectedReturnDate = null;
          this.tariffHotelCriteria.hotelToDate = null;
          this.resetReturnCalendar = true;
          this.returnInitialDate = [];
          return;
        }
        if (this.selectedPickupDate) {
          this.tariffHotelCriteria.hotelFromDate = this.formattedDate(
            this.selectedPickupDate
          );
        }
      } else {
        this.selectedReturnDate = event[0];
        if (
          this.selectedPickupDate &&
          this.selectedPickupDate.getTime() > this.selectedReturnDate.getTime()
        ) {
          this.selectedReturnDate = null;
          this.tariffHotelCriteria.hotelFromDate = null;
          this.resetReturnCalendar = true;
          this.returnInitialDate = [];
          return;
        }
        if (this.selectedReturnDate) {
          this.tariffHotelCriteria.hotelToDate = this.formattedDate(
            this.selectedReturnDate
          );
        }
      }
    } else {
      // executes if invalid date and time is typed
      if (isPickup) {
        this.selectedPickupDate = null;
        this.tariffHotelCriteria.hotelFromDate = null;
      } else {
        this.selectedReturnDate = null;
        this.tariffHotelCriteria.hotelToDate = null;
      }
    }
  }

  formattedDate(date) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.formattedDate');
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    return dd + '/' + mm + '/' + yyyy;
  }

  resetAllInputs() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.resetAllInputs');
    this.tariffHotelCriteria.reset();
    this.resetTariffYear();
    this.resetCurrencies();
    this.resetDates();
    // resets all type-ahead fields
    this.typeAheadService.resetTypeAheadStore({ URL: true });
    this.tariffResultService.initialLoading = true;
    this.tariffResultService.sortDirection = 'neutral';
    this.tariffResultService.pageNumber = 1;
  }

  resetDates(tariffYear = null) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.resetDates');
    if (!tariffYear) {
      this.selectedPickupDate = new Date();
      this.selectedReturnDate = this.getSixMonthsFromNow(
        this.selectedPickupDate
      );
      this.returnInitialDate = [this.selectedReturnDate];
      this.tariffHotelCriteria.hotelToDate = this.formattedDate(
        this.selectedReturnDate
      );
      this.pickUpInitialDate = [this.selectedPickupDate];
      this.tariffHotelCriteria.hotelFromDate = this.formattedDate(
        this.selectedPickupDate
      );
    } else {
      const hasInitialTariffYear = this.initialTariffYear === tariffYear;
      const date = new Date();
      if (!hasInitialTariffYear) {
        date.setFullYear(tariffYear);
        date.setMonth(date.getMonth());
        date.setDate(1);
      }
      this.selectedPickupDate = date;
      this.selectedReturnDate = this.getSixMonthsFromNow(
        this.selectedPickupDate
      );
      this.returnInitialDate = [this.selectedReturnDate];
      this.tariffHotelCriteria.hotelToDate = this.formattedDate(
        this.selectedReturnDate
      );
      this.pickUpInitialDate = [this.selectedPickupDate];
      this.tariffHotelCriteria.hotelFromDate = this.formattedDate(
        this.selectedPickupDate
      );
    }
  }

  resetCurrencies() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.resetCurrencies');
    this.currencies.forEach((ele) => {
      ele.selected = ele.code === this.defCurrency.code;
    });
    this.currencies = SurfUtilService.getDeepClone(this.currencies);
    if (this.tariffHotelCriteria) {
      this.tariffHotelCriteria.hotelCurrency = this.defCurrency.code;
    }
  }

  searchTariffs() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.searchTariffs');
    if (!this.validateInputs()) {
      return;
    }

    this.tariffResultService.hasMoreResults = true;
    this.tariffResultService.pageNumber = 1;
    this.tariffResultService.initialLoading = false;

    this.tariffApiService.searchTariffHotelCriteria(
      this.tariffHotelCriteria,
      this.keyParams
    );
  }

  validateInputs(): boolean {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.validateInputs');
    let isValid = true;

    if (
      !this.tariffHotelCriteria.hotelCountry
    ) {
      isValid = false;
    }

    if (!this.tariffHotelCriteria.hotelCurrency) {
      isValid = false;
    }

    if (!this.tariffHotelCriteria.hotelTariffYear) {
      isValid = false;
    }

    if (
      !this.tariffHotelCriteria.hotelFromDate ||
      !this.tariffHotelCriteria.hotelToDate
    ) {
      isValid = false;
    }

    // from - to date validation
    if (
      this.tariffHotelCriteria.hotelFromDate &&
      this.tariffHotelCriteria.hotelToDate
    ) {
      const fromDate = new Date(this.tariffHotelCriteria.hotelFromDate);
      const toDate = new Date(this.tariffHotelCriteria.hotelToDate);
      if (fromDate.getTime() > toDate.getTime()) {
        isValid = false;
      }
    }

    return isValid;
  }

  private updateCityRetrieveURL() {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.updateCityRetrieveURL');
    const param1 =
      '?dataTypes=CITY&userInput=[UI]&aggrLevel=NA&locale=en&matchType=CODE~NAME&expand=all&resultCount=50';
    let param2: string;
    let param3: string;

    if (this.tariffHotelCriteria.hotelCountry) {
      param2 = '&parentType=' + PARENT_TYPE.COUNTRY;
      param3 = '&parentCode=' + this.tariffHotelCriteria.hotelCountry;
    } else {
      param2 = '&parentType=' + 'NA';
      param3 = '';
    }

    this.cityURL = this.STATIC_DATA_URL_BASE + param1 + param2 + param3;
  }

  selectHotel(data, type: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.selectHotel');
    this.selectType(data, type, 'hotelName');

    // // set country and city
    const hotel = this.typeAheadService.getTypeAheadByType('hotel');
    if (hotel && hotel.selected && hotel.ontologies?.[0]?.query) {
      const country = data?.parent?.find(
        (item) => item.type === 'COUNTRY' || item.type === 20
      );
      if (country) {
        this.tariffHotelCriteria.hotelCountry = country.code;
        this.selectCountry(country, 'country');
      }

      const city = data?.parent?.find((item) => item.type === 'CITY' || item.type === 40);
      if (city) {
        this.tariffHotelCriteria.hotelCity = city.code;
        this.selectCity(city, 'city');
      }
    }
  }

  selectCountry(data, type: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.selectCountry');
    this.selectType(data, type, 'countryName');
    this.updateCityRetrieveURL();
    this.typeAheadService.updateTypeAheadStore('city', { URL: this.cityURL });
    this.typeAheadService.resetTypeAheadItem('city');
    this.tariffHotelCriteria.hotelCity = null;
  }

  selectCity(data, type: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.selectCity');
    this.selectType(data, type, 'cityName');
  }

  selectType(data, type: Type, typeName?: TypeName) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.selectType');
    this.typeAheadService.selectType(
      data,
      type,
      this.tariffHotelCriteria,
      typeName
    );
  }

  onClickOutside(type: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.onClickOutside');
    const typeCopy = this.typeAheadService.getTypeAheadByType(type);

    if (this.tariffHotelCriteria && typeCopy && typeCopy.ontologies && typeCopy.ontologies.length > 0) {
      const sameDisplayText = typeCopy.ontologies[0].displayText === typeCopy.selected.displayText;
      if (!sameDisplayText && typeCopy.selected.query?.length >= this.MIN_SEARCH_QUERY_LENGTH) {
        typeCopy.selected.displayText = typeCopy.ontologies[0].displayText;
      }
    }
  }

  returnedValues(data, type: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.returnedValues');
    if (type === 'hotel' && Array.isArray(data)) {
      // push what the user types into the ontologies array
      const typeCopy = this.typeAheadService.getTypeAheadByType(type);
      if (typeCopy?.selected?.query?.length >= this.MIN_SEARCH_QUERY_LENGTH) {
        const userEnteredQuery = {
          code: typeCopy?.selected?.displayText,
          name: typeCopy?.selected?.displayText,
          type: 80,
          parent: [],
          attributes: []
        };
        data.unshift(userEnteredQuery);
      }
    }
    if (Array.isArray(data) && data.length === 0) {
      const criteriaField = this.typeAheadService.getHotelCriteriaFieldMappedToType(type);
      this.tariffHotelCriteria[criteriaField] = '';
    }

    this.typeAheadService.returnedValues(data, type, this.tariffHotelCriteria);
  }

  returnedSearchQueryForType(event, type: Type) {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.returnedSearchQueryForType');
    this.typeAheadService.updateTypeAheadStore(type, {
      searchInitialized: event && event.length >= 1,
    });

    if (event && event.length < this.MIN_SEARCH_QUERY_LENGTH) {
      const criteriaField = this.typeAheadService.getHotelCriteriaFieldMappedToType(type);
      const typeCopy = this.typeAheadService.getTypeAheadByType(type);

      if (this.tariffHotelCriteria && typeCopy && typeCopy.ontologies && typeCopy.ontologies.length > 0) {
        const sameDisplayText = typeCopy.ontologies[0].displayText === typeCopy.selected.displayText;
        if (!sameDisplayText) {
          this.tariffHotelCriteria[criteriaField] = '';
        }
      } else if (this.tariffHotelCriteria && typeCopy && typeCopy.selected && typeCopy.selected.displayText) {
        this.tariffHotelCriteria[criteriaField] = '';
      }
    }
  }

  generateStaticDataUrl(dataType: string): string {
    TraceXInterceptor.interceptTrace('dashboard/projects/surf-tariff-search/src/lib/tariff-search-criteria/tariff-search-criteria.component.ts.TariffSearchCriteriaComponent.generateStaticDataUrl');
    return (
      this.STATIC_DATA_URL_BASE +
      `?dataTypes=${dataType}&userInput=[UI]&aggrLevel=NA&locale=en&matchType=CODE~NAME&expand=all&resultCount=` +
      `${this.STATIC_DATA_FETCH_SIZE}`
    );
  }
}
